import React, { useMemo, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Divider } from "@/components/Divider";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function MortgageCalculator() {
  const [price, setPrice] = useState(450000);
  const [down, setDown] = useState(90000);
  const [rate, setRate] = useState(6.625);
  const [years, setYears] = useState(30);
  const [tax, setTax] = useState(4800);
  const [insurance, setInsurance] = useState(1800);
  const [hoa, setHoa] = useState(0);

  const calc = useMemo(() => {
    const principal = Math.max(0, price - down);
    const monthlyRate = rate / 100 / 12;
    const n = years * 12;
    const pi =
      monthlyRate > 0
        ? (principal * (monthlyRate * Math.pow(1 + monthlyRate, n))) /
          (Math.pow(1 + monthlyRate, n) - 1)
        : principal / n;
    const monthlyTax = tax / 12;
    const monthlyIns = insurance / 12;
    const monthlyHoa = hoa / 12;
    const total = pi + monthlyTax + monthlyIns + monthlyHoa;
    const totalInterest = pi * n - principal;
    return {
      principal,
      pi: isFinite(pi) ? pi : 0,
      monthlyTax,
      monthlyIns,
      monthlyHoa,
      total: isFinite(total) ? total : 0,
      totalInterest: isFinite(totalInterest) ? totalInterest : 0,
    };
  }, [price, down, rate, years, tax, insurance, hoa]);

  const chartData = [
    { name: "Principal & Interest", value: Math.round(calc.pi), color: "#00A651" },
    { name: "Property Tax", value: Math.round(calc.monthlyTax), color: "#E88C2F" },
    { name: "Home Insurance", value: Math.round(calc.monthlyIns), color: "#ED1C24" },
    { name: "HOA", value: Math.round(calc.monthlyHoa), color: "#F26522" },
  ].filter((d) => d.value > 0);

  return (
    <div data-testid="page-calculator" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651] mb-4">Mortgage Calculator</p>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl text-black tracking-tight">
          Find your monthly payment.
        </h1>
        <Divider className="my-6" />
        <p className="font-sub italic text-lg text-gray-600 max-w-2xl">
          Adjust the inputs and watch the breakdown update in real time. Estimates only;
          your actual rate and payment depend on credit, program, and property.
        </p>

        <div className="grid lg:grid-cols-12 gap-10 mt-12">
          {/* INPUTS */}
          <div className="lg:col-span-7 space-y-7">
            <NumField
              testid="calc-price"
              label="Home Price"
              value={price}
              onChange={setPrice}
              min={50000}
              max={2500000}
              step={5000}
              format={fmt}
            />
            <NumField
              testid="calc-down"
              label="Down Payment"
              value={down}
              onChange={setDown}
              min={0}
              max={price}
              step={1000}
              format={fmt}
              suffix={`${price > 0 ? ((down / price) * 100).toFixed(1) : 0}% down`}
            />
            <NumField
              testid="calc-rate"
              label="Interest Rate"
              value={rate}
              onChange={setRate}
              min={2}
              max={12}
              step={0.125}
              format={(v) => `${v.toFixed(3)}%`}
            />
            <NumField
              testid="calc-term"
              label="Loan Term (Years)"
              value={years}
              onChange={setYears}
              min={5}
              max={40}
              step={1}
              format={(v) => `${v} yrs`}
            />
            <div className="grid sm:grid-cols-3 gap-5">
              <SmallField testid="calc-tax" label="Annual Property Tax" value={tax} onChange={setTax} />
              <SmallField testid="calc-ins" label="Annual Insurance" value={insurance} onChange={setInsurance} />
              <SmallField testid="calc-hoa" label="Annual HOA" value={hoa} onChange={setHoa} />
            </div>
          </div>

          {/* RESULT */}
          <div className="lg:col-span-5">
            <div className="sticky top-28 bg-white border border-gray-200 rounded-sm overflow-hidden">
              <div className="bg-black text-white p-6">
                <p className="text-xs uppercase tracking-[0.25em] text-[#E88C2F] font-bold">
                  Estimated Monthly Payment
                </p>
                <div
                  className="font-heading font-bold text-5xl mt-2"
                  data-testid="calc-monthly-total"
                >
                  {fmt(calc.total)}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Loan amount {fmt(calc.principal)} · {years}yr @ {rate.toFixed(3)}%
                </p>
              </div>
              <div className="p-6">
                <div className="h-56">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={chartData}
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {chartData.map((d) => (
                          <Cell key={d.name} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="mt-4 space-y-2">
                  {chartData.map((d) => (
                    <li key={d.name} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3" style={{ background: d.color }} />
                        <span className="text-gray-700">{d.name}</span>
                      </span>
                      <span className="font-heading font-bold">{fmt(d.value)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-gray-200 text-xs text-gray-500">
                  Total interest over loan life:&nbsp;
                  <span className="font-heading font-bold text-black">{fmt(calc.totalInterest)}</span>
                </div>
                <Link
                  to="/apply"
                  data-testid="calc-apply-btn"
                  className="mt-6 block bg-[#00A651] hover:bg-[#008f45] text-white text-center font-bold uppercase tracking-wider text-sm px-6 py-3 rounded-sm transition-colors"
                >
                  Get Pre-Approved
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function NumField({ testid, label, value, onChange, min, max, step, format, suffix }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <Label className="text-sm font-bold text-black uppercase tracking-wider">{label}</Label>
        <span className="font-heading font-bold text-xl text-black" data-testid={`${testid}-display`}>
          {format(value)}
        </span>
      </div>
      <Slider
        data-testid={testid}
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        min={min}
        max={max}
        step={step}
      />
      {suffix && <p className="text-xs text-gray-500 mt-2">{suffix}</p>}
    </div>
  );
}

function SmallField({ testid, label, value, onChange }) {
  return (
    <div>
      <Label className="text-xs font-bold text-black uppercase tracking-wider">{label}</Label>
      <Input
        type="number"
        value={value}
        min={0}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        data-testid={testid}
        className="mt-2 rounded-sm"
      />
    </div>
  );
}
